// src/pages/ServiceDetail/ServiceDetailPage.tsx

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftOutlined, 
  ReloadOutlined,
  ExportOutlined,
  SettingOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  SyncOutlined,
  MoreOutlined
} from '@ant-design/icons';
import { ServiceStatusBadge, ConfirmationModal } from '../../components/shared';
import { AnyService } from '../../types/service.type';
import { useServiceActions } from '../../hooks/useServiceActions';
import { getServiceConfig } from '../../config/serviceConfig';
import { getServiceAge } from '../../utils/serviceHelpers';
import ServiceDetailTabs from './components/ServiceDetailTabs';

const ServiceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStopModal, setShowStopModal] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [activeTab, setActiveTab] = useState('events');

  // Mock service data
  const service: AnyService = {
    id: id || '1',
    name: 'my-blog',
    type: 'wordpress',
    status: 'running',
    health: 'degraded',
    primaryUrl: 'https://my-blog.kubiy.cloud',
    adminUrl: 'https://my-blog.kubiy.cloud/wp-admin',
    createdAt: new Date('2024-01-15'),
    updatedAt: new Date(),
    lastActionAt: new Date(),
    instanceType: 'wp-medium',
    region: 'us-east-1',
    environment: 'production',
    tags: ['production', 'wordpress'],
    phpVersion: 'PHP 8.4',
    wpVersion: '6.4',
    sslEnabled: true,
    config: {} as any,
  };

  const {
    isLoading,
    currentAction,
    startService,
    stopService,
    restartService,
    deleteService,
    canStart,
    canStop,
    canRestart,
    canDelete,
    canVisit,
    visitUrl,
  } = useServiceActions({
    service,
    onSuccess: () => {
      console.log('Action completed');
    },
  });

  const config = getServiceConfig(service.type);

  const handleDelete = async () => {
    await deleteService();
    setShowDeleteModal(false);
    navigate('/wordpress/environments');
  };

  const handleStop = async () => {
    await stopService();
    setShowStopModal(false);
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-6">
      {/* Breadcrumb & Actions */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeftOutlined />
            <span className="text-sm font-medium">Back</span>
          </button>
          <div className="h-4 w-px bg-gray-300 dark:bg-gray-600"></div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{service.name}</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {config.label} • Created {getServiceAge(service.createdAt)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Visit Site Button (Primary) */}
          {canVisit && visitUrl && (
            <button
              onClick={() => window.open(visitUrl, '_blank')}
              className="flex items-center gap-2 px-5 py-2.5 bg-black text-white dark:bg-white dark:text-black rounded-lg font-medium hover:bg-dark-bg transition-colors"
            >
              <ExportOutlined />
              Visit Site
            </button>
          )}

          {/* Start/Stop Button */}
          {canStart && (
            <button
              onClick={startService}
              disabled={isLoading}
              className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              <PlayCircleOutlined />
              Start
            </button>
          )}

          {canStop && (
            <button
              onClick={() => setShowStopModal(true)}
              disabled={isLoading}
              className="flex items-center gap-2 px-5 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <PauseCircleOutlined />
              Stop
            </button>
          )}

          {/* More Actions Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowActionsMenu(!showActionsMenu)}
              className="p-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <MoreOutlined className="text-lg" />
            </button>

            {showActionsMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowActionsMenu(false)}
                ></div>
                <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-20">
                  {canRestart && (
                    <button
                      onClick={() => {
                        restartService();
                        setShowActionsMenu(false);
                      }}
                      disabled={isLoading}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-3"
                    >
                      <SyncOutlined />
                      Restart Service
                    </button>
                  )}
                  <button
                    onClick={() => {
                      navigate(`/services/${service.id}/settings`);
                      setShowActionsMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-3"
                  >
                    <SettingOutlined />
                    Settings
                  </button>
                  <button
                    onClick={() => window.location.reload()}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-3"
                  >
                    <ReloadOutlined />
                    Refresh
                  </button>
                  {canDelete && (
                    <>
                      <hr className="my-1 border-gray-200 dark:border-gray-700" />
                      <button
                        onClick={() => {
                          setShowDeleteModal(true);
                          setShowActionsMenu(false);
                        }}
                        disabled={isLoading}
                        className="w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50 flex items-center gap-3"
                      >
                        <DeleteOutlined />
                        Delete Service
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Status Banner (if degraded or error) */}
      {(service.health === 'degraded' || service.health === 'unhealthy') && (
        <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
          <div className="flex items-center gap-3">
            <span className="text-yellow-700 text-xl">⚠️</span>
            <div>
              <p className="text-sm font-semibold text-yellow-900">Service Health Issue</p>
              <p className="text-sm text-yellow-800 mt-0.5">
                This service is experiencing health issues. Check the Events tab for details.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-12 gap-6">
        {/* Left Column - Main Info */}
        <div className="col-span-8 space-y-6">
          {/* Overview Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Overview</h2>
              <ServiceStatusBadge status={service.status} size="md" showLabel />
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="text-sm text-gray-500 mb-1 block">Service ID</label>
                  <div className="flex items-center gap-2">
                    <code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono dark:text-white">
                      {service.id}
                    </code>
                    <button
                      onClick={() => navigator.clipboard.writeText(service.id)}
                      className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                      title="Copy ID"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-sm text-gray-500 mb-1 block">Type</label>
                  <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                    {config.label}
                  </p>
                </div>

                <div>
                  <label className="text-sm text-gray-500 mb-1 block">Environment</label>
                  <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                    {service.environment || 'Not set'}
                  </p>
                </div>

                <div>
                  <label className="text-sm text-gray-500 mb-1 block">Region</label>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {service.region || 'Not set'}
                  </p>
                </div>

                {service.primaryUrl && (
                  <div className="col-span-2">
                    <label className="text-sm text-gray-500 mb-1 block">Primary URL</label>
                    <a
                      href={service.primaryUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    >
                      {service.primaryUrl}
                      <ExportOutlined className="text-xs" />
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <ServiceDetailTabs 
            service={service}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
        </div>

        {/* Right Column - Quick Info & Actions */}
        <div className="col-span-4 space-y-6">
          {/* Health Status Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">Health Status</h3>
            </div>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-gray-600 dark:text-gray-400">Overall Health</span>
                <div className="flex items-center gap-2">
                  <span
                    className={`w-2.5 h-2.5 rounded-full ${
                      service.health === 'healthy'
                        ? 'bg-green-500'
                        : service.health === 'degraded'
                        ? 'bg-yellow-500'
                        : 'bg-red-500'
                    }`}
                  ></span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                    {service.health}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-gray-600 dark:text-gray-400">Status</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                  {service.status}
                </span>
              </div>

              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-gray-600 dark:text-gray-400">Last Updated</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {getServiceAge(service.updatedAt)}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Created</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {getServiceAge(service.createdAt)}
                </span>
              </div>
            </div>
          </div>

          {/* Configuration Card (for WordPress) */}
          {service.type === 'wordpress' && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">Configuration</h3>
              </div>
              <div className="p-6 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Instance Type</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {service.instanceType}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">PHP Version</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {service.phpVersion}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">WordPress</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {service.wpVersion}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">SSL</span>
                  <span className="text-sm font-medium text-green-600">
                    {service.sslEnabled ? '✓ Enabled' : '✗ Disabled'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Quick Links Card */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Quick Links</h3>
            <div className="space-y-2">
              {canVisit && visitUrl && (
                <a
                  href={visitUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
                >
                  <span>Visit Site</span>
                  <ExportOutlined />
                </a>
              )}
              {service.adminUrl && (
                <a
                  href={service.adminUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
                >
                  <span>Admin Panel</span>
                  <ExportOutlined />
                </a>
              )}
            </div>
          </div>

          {/* Tags */}
          {service.tags && service.tags.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">Tags</h3>
              </div>
              <div className="p-6">
                <div className="flex flex-wrap gap-2">
                  {service.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-medium rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Service"
        message={`Are you sure you want to delete "${service.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        requireInput
        expectedInput={service.name}
        loading={isLoading && currentAction === 'delete'}
      />

      <ConfirmationModal
        isOpen={showStopModal}
        onClose={() => setShowStopModal(false)}
        onConfirm={handleStop}
        title="Stop Service"
        message={`Are you sure you want to stop "${service.name}"?`}
        confirmText="Stop"
        loading={isLoading && currentAction === 'stop'}
      />
    </div>
  );
};

export default ServiceDetailPage;